import { useTranslation } from "react-i18next"
import type { ToastItem, ToastSeverity } from "@/features/toast/types"

const severityIcon: Record<ToastSeverity, string> = {
  info: "ri-information-line",
  success: "ri-checkbox-circle-line",
  warning: "ri-alert-line",
  error: "ri-error-warning-line",
}

const severityStyles: Record<
  ToastSeverity,
  { ring: string; icon: string; title: string; role: "status" | "alert"; ariaLive: "polite" | "assertive" }
> = {
  info: {
    ring: "border-gray-200",
    icon: "text-gray-500",
    title: "text-gray-900",
    role: "status",
    ariaLive: "polite",
  },
  success: {
    ring: "border-emerald-200",
    icon: "text-emerald-600",
    title: "text-gray-900",
    role: "status",
    ariaLive: "polite",
  },
  warning: {
    ring: "border-amber-200",
    icon: "text-amber-600",
    title: "text-gray-900",
    role: "alert",
    ariaLive: "polite",
  },
  error: {
    ring: "border-brand-500/30",
    icon: "text-brand-500",
    title: "text-gray-900",
    role: "alert",
    ariaLive: "assertive",
  },
}

type Props = {
  toasts: ToastItem[]
  onDismiss: (id: string) => void
}

export const ToastViewport = ({ toasts, onDismiss }: Props) => {
  const { t } = useTranslation()

  if (toasts.length === 0) return null

  return (
    <div
      aria-label={t("toast.regionAria")}
      className="fixed z-[80] pointer-events-none inset-x-0 top-4 flex flex-col items-center gap-2 px-4 sm:top-auto sm:bottom-4 sm:right-4 sm:left-auto sm:items-end sm:max-w-sm"
    >
      {toasts.map((toast) => {
        const styles = severityStyles[toast.severity]
        const interactive = Boolean(toast.onClick)

        const handleActivate = () => {
          toast.onClick?.()
          onDismiss(toast.id)
        }

        const handleKeyDown = (e: React.KeyboardEvent) => {
          if (!interactive) return
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            handleActivate()
          }
        }

        return (
          <div
            key={toast.id}
            role={styles.role}
            aria-live={styles.ariaLive}
            aria-atomic="true"
            className={`pointer-events-auto w-full sm:min-w-[20rem] bg-white border ${styles.ring} rounded-2xl shadow-lg px-4 py-3 flex items-start gap-3 transition-all`}
          >
            <div className={`mt-0.5 w-5 h-5 flex items-center justify-center ${styles.icon}`} aria-hidden>
              <i className={`${severityIcon[toast.severity]} text-lg`} />
            </div>
            <div
              className={`min-w-0 flex-1 ${interactive ? "cursor-pointer" : ""}`}
              role={interactive ? "button" : undefined}
              tabIndex={interactive ? 0 : undefined}
              onClick={interactive ? handleActivate : undefined}
              onKeyDown={handleKeyDown}
            >
              {toast.title ? (
                <p className={`text-sm font-semibold ${styles.title} break-words`}>{toast.title}</p>
              ) : null}
              {toast.description ? (
                <p className="text-xs text-gray-600 mt-0.5 leading-relaxed break-words">{toast.description}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(toast.id)}
              aria-label={t("toast.close")}
              className="shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 transition-colors"
            >
              <i className="ri-close-line text-base" aria-hidden />
            </button>
          </div>
        )
      })}
    </div>
  )
}
